<template>
  <div
    class="base-toast"
    :class="[`base-toast--${type}`, { 'is-paused': paused }]"
    role="alert"
    @mouseenter="paused = true"
    @mouseleave="paused = false"
  >
    <div class="base-toast-icon">
      <BaseIcon :name="iconName" size="18" />
    </div>

    <div class="base-toast-body">
      <p class="base-toast-content" v-html="content" />
    </div>

    <button
      v-if="dismissible"
      type="button"
      class="base-toast-close"
      aria-label="Fechar"
      @click="dismiss"
    >
      <BaseIcon name="mdi-close" size="14" />
    </button>

    <span
      v-if="duration > 0"
      class="base-toast-progress"
      :style="progressStyle"
    />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';

const ICONS = {
  success: 'mdi-check',
  error:   'mdi-alert-circle-outline',
  warning: 'mdi-alert-outline',
  info:    'mdi-information-outline',
};

export default {
  name: 'BaseToast',
  components: { BaseIcon },
  props: {
    id:          { type: [String, Number], required: true },
    type:        { type: String, default: 'info' }, // success | error | warning | info
    content:     { type: String, default: '' },
    duration:    { type: Number, default: 5000 },
    dismissible: { type: Boolean, default: true },
  },
  data() {
    return { paused: false, elapsed: 0, lastTick: null, frameId: null };
  },
  computed: {
    iconName() { return ICONS[this.type] || ICONS.info; },
    progressStyle() {
      const pct = Math.min(100, (this.elapsed / this.duration) * 100);
      return { width: `${100 - pct}%` };
    },
  },
  mounted() {
    if (this.duration > 0) this.start();
  },
  beforeDestroy() {
    if (this.frameId) cancelAnimationFrame(this.frameId);
  },
  methods: {
    start() {
      this.lastTick = performance.now();
      const tick = (now) => {
        const delta = now - this.lastTick;
        this.lastTick = now;
        if (!this.paused) this.elapsed += delta;
        if (this.elapsed >= this.duration) {
          this.dismiss();
          return;
        }
        this.frameId = requestAnimationFrame(tick);
      };
      this.frameId = requestAnimationFrame(tick);
    },
    dismiss() {
      this.$emit('dismiss', this.id);
    },
  },
};
</script>

<style scoped>
.base-toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 280px;
  max-width: 380px;
  padding: 12px 14px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-left-width: 4px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  pointer-events: auto;
  font-family: var(--font-body);
}

.base-toast-icon {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
}

.base-toast-body { flex: 1; min-width: 0; }
.base-toast-content {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--color-text);
  word-break: break-word;
}
.base-toast-content ::v-deep b,
.base-toast-content ::v-deep strong {
  display: block;
  font-weight: 600;
  color: var(--color-text);
  font-size: var(--text-sm);
  margin-bottom: 2px;
}

.base-toast-close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  border-radius: var(--radius-sm);
  color: var(--color-text-muted);
  cursor: pointer;
  margin-top: 1px;
  transition: background-color var(--duration-fast) var(--ease), color var(--duration-fast) var(--ease);
}
.base-toast-close:hover { background: var(--color-surface-hover); color: var(--color-text); }

.base-toast-progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 100%;
  background: currentColor;
  opacity: 0.5;
}

/* Tipos */
.base-toast--success {
  border-left-color: var(--color-success);
  color: var(--color-success);
}
.base-toast--success .base-toast-icon { background: var(--color-success-soft); }

.base-toast--error {
  border-left-color: var(--color-danger);
  color: var(--color-danger);
}
.base-toast--error .base-toast-icon { background: var(--color-danger-soft); }

.base-toast--warning {
  border-left-color: var(--color-warning);
  color: var(--color-warning);
}
.base-toast--warning .base-toast-icon { background: var(--color-warning-soft); }

.base-toast--info {
  border-left-color: var(--color-info);
  color: var(--color-info);
}
.base-toast--info .base-toast-icon { background: var(--color-info-soft); }
</style>
